import React, { useState } from "react";
import { toast } from "sonner";
import { Send, X, Loader2 } from "lucide-react";
import { apiClient, formatApiError } from "../../lib/api";

export const SendEmailDialog = ({ application, onSent }) => {
    const [open, setOpen] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [includeLink, setIncludeLink] = useState(false);
    const [sending, setSending] = useState(false);

    const openDialog = async () => {
        setOpen(true);
        setSubject("");
        setBody("");
        setIncludeLink(false);
        try {
            const { data } = await apiClient.get("/admin/message-templates");
            setTemplates(data.items || []);
        } catch (e) {
            console.error("Templates load failed:", e);
        }
    };

    const applyTemplate = (id) => {
        const tpl = templates.find((t) => t.id === id);
        if (tpl) {
            setSubject(tpl.subject);
            setBody(tpl.body);
        }
    };

    const send = async () => {
        if (!subject.trim() || !body.trim()) {
            toast.error("Bitte Betreff und Text ausfüllen.");
            return;
        }
        setSending(true);
        try {
            const { data } = await apiClient.post(`/applications/${application.id}/send-email`, { subject, body, include_manage_link: includeLink });
            if (data.ok) {
                toast.success(`E-Mail an ${application.email} gesendet.`);
                onSent(data.email);
                setOpen(false);
            } else {
                toast.error(`E-Mail fehlgeschlagen: ${data.error || "Unbekannter Fehler"}`, { duration: 10000 });
                onSent(data.email);
            }
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Senden fehlgeschlagen.");
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            <button
                data-testid="open-send-email-btn"
                onClick={openDialog}
                className="inline-flex items-center gap-2 text-sm font-medium text-moebus-deep bg-white border border-moebus-border hover:bg-slate-50 px-4 py-2 rounded-md"
            >
                <Send size={14} /> Individuelle E-Mail senden
            </button>
            {open && (
                <div
                    data-testid="send-email-dialog"
                    className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={(e) => e.target === e.currentTarget && setOpen(false)}
                >
                    <div className="bg-white w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-lg shadow-2xl">
                        <div className="sticky top-0 bg-white border-b border-moebus-border px-5 py-3.5 flex items-center justify-between">
                            <h3 className="font-heading font-bold text-moebus-deep">
                                E-Mail an {application.first_name} {application.last_name}
                            </h3>
                            <button data-testid="send-email-close" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-5 grid gap-4">
                            <label className="grid gap-1 text-xs font-medium text-slate-500">
                                Vorlage wählen (optional)
                                <select
                                    data-testid="email-template-select"
                                    defaultValue=""
                                    onChange={(e) => applyTemplate(e.target.value)}
                                    className="text-sm font-normal border border-moebus-border rounded-md px-3 py-2 bg-white"
                                >
                                    <option value="">– Ohne Vorlage / frei schreiben –</option>
                                    {templates.map((t) => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="grid gap-1 text-xs font-medium text-slate-500">
                                Betreff
                                <input
                                    data-testid="email-subject-input"
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    className="text-sm font-normal border border-moebus-border rounded-md px-3 py-2"
                                />
                            </label>
                            <label className="grid gap-1 text-xs font-medium text-slate-500">
                                Nachricht
                                <textarea
                                    data-testid="email-body-input"
                                    value={body}
                                    onChange={(e) => setBody(e.target.value)}
                                    rows={10}
                                    className="text-sm font-normal border border-moebus-border rounded-md px-3 py-2"
                                />
                            </label>
                            <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
                                <input
                                    data-testid="email-include-link-checkbox"
                                    type="checkbox"
                                    checked={includeLink}
                                    onChange={(e) => setIncludeLink(e.target.checked)}
                                    className="mt-0.5 accent-emerald-600"
                                />
                                <span>
                                    <span className="font-medium text-moebus-deep">Persönlichen Upload-Link anhängen</span>
                                    <br />
                                    Hängt automatisch den Link an, über den {application.first_name} Unterlagen nachreichen kann.
                                </span>
                            </label>
                            <p className="text-[11px] text-slate-400">
                                Platzhalter werden automatisch ersetzt: {"{vorname}"}, {"{nachname}"}, {"{position}"}.
                                Die E-Mail geht an <span className="font-medium">{application.email}</span> und wird im Verlauf gespeichert.
                            </p>
                            <button
                                data-testid="send-email-btn"
                                onClick={send}
                                disabled={sending}
                                className="inline-flex items-center justify-center gap-2 bg-moebus-green hover:bg-emerald-700 text-white font-medium px-4 py-2.5 rounded-md text-sm disabled:opacity-60"
                            >
                                {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Jetzt senden
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
